import React, { FC, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
  EmissionFactorDto,
  EmissionFactorSource,
  UpdateEmissionFactorDto,
} from "@green0meter/api-client";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { isEmpty } from "lodash";

import {
  emissionFactorTypeOptions,
  schema,
  sourceOptions,
} from "./EmissionFactorEditor.const";
import { SearchQuestions } from "./components";

import { Drawer } from "@components/common";
import {
  Button,
  Stack,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Chip,
  Card,
  CardContent,
} from "@components/base";
import { useUserStore } from "@store/user";
import {
  useGetQuestionUnits,
  useCreateEmissionFactor,
  useUpdateEmissionFactor,
} from "@api";

interface EmissionFactorEditor {
  emissionFactor?: EmissionFactorDto;
  isOpen: boolean;
  onClose: () => void;
  customEmissionFactor?: boolean;
}

const EmissionFactorEditor: FC<EmissionFactorEditor> = ({
  emissionFactor,
  isOpen,
  onClose,
  customEmissionFactor = false,
}) => {
  const { user } = useUserStore();
  const { t } = useTranslation();
  const { units } = useGetQuestionUnits(user);
  const { createEmissionFactor, createEmissionFactorEnabled } =
    useCreateEmissionFactor(user);
  const { updateEmissionFactor, updateEmissionFactorEnabled } =
    useUpdateEmissionFactor(user);

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<UpdateEmissionFactorDto>({
      resolver: yupResolver(schema),
    });

  const { fields, append, remove } = useFieldArray({ control, name: "pairs" });

  const isEdit = !!emissionFactor?.id;

  useEffect(() => {
    if (emissionFactor) {
      reset({
        factor: emissionFactor.id ? emissionFactor.factor : undefined,
        scope: emissionFactor.id ? emissionFactor.scope : 1,
        type: emissionFactor.type,
        unitId: emissionFactor.unit.id || units?.[0].id,
        source: emissionFactor.source ?? EmissionFactorSource.Defra,
        sourceDetail: emissionFactor.sourceDetail,
        year: emissionFactor.year,
        pairs: emissionFactor.pairs.map((pair) => ({
          id: pair.id,
          questionCode: pair.questionCode,
        })),
      });
    }
  }, [emissionFactor]);

  useEffect(() => {
    if (units && !isEmpty(units) && !watch("unitId")) {
      setValue("unitId", units[0].id);
    }
  }, [units]);

  const onSubmit = (dto: UpdateEmissionFactorDto): void => {
    if (isEdit && emissionFactor?.id) {
      if (updateEmissionFactorEnabled) {
        updateEmissionFactor({ emissionFactorId: emissionFactor.id, dto });
      }
    } else {
      if (createEmissionFactorEnabled) {
        createEmissionFactor({ ...dto, customEmissionFactor });
      }
    }
    onClose();
  };

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={
        isEdit
          ? t("emissionFactorEditor.titleEdit")
          : t("emissionFactorEditor.titleCreate")
      }
      content={
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t("emissionFactorEditor.factorLabel")}
            </Typography>
            <Controller
              name="factor"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  type="number"
                  variant="filled"
                  fullWidth
                  placeholder={t("emissionFactorEditor.factorPlaceholder")}
                  error={!!error}
                  {...field}
                />
              )}
            />

            <Typography variant="body2" color="text.secondary">
              {t("emissionFactorEditor.scopeLabel")}
            </Typography>
            <Controller
              name="scope"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  type="number"
                  variant="filled"
                  fullWidth
                  placeholder={t("emissionFactorEditor.scopePlaceholder")}
                  error={!!error}
                  {...field}
                />
              )}
            />

            <Typography variant="body2" color="text.secondary">
              {t("emissionFactorEditor.typeLabel")}
            </Typography>
            <Controller
              name="type"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  variant="filled"
                  fullWidth
                  value={field.value}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  error={!!error}
                >
                  {emissionFactorTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {t(`emissionFactorEditor.${opt.labelKey}`)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />

            <Typography variant="body2" color="text.secondary">
              {t("emissionFactorEditor.unitLabel")}
            </Typography>
            <Controller
              name="unitId"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  variant="filled"
                  fullWidth
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  error={!!error}
                >
                  {units?.map((unit) => (
                    <MenuItem key={unit.id} value={unit.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>{unit.name}</Typography>
                        <Chip label={unit.abbreviation} size="small" color="blue1" />
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              )}
            />

            <Typography variant="body2" color="text.secondary">
              {t("emissionFactorEditor.sourceLabel")}
            </Typography>
            <Controller
              name="source"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  variant="filled"
                  fullWidth
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  error={!!error}
                >
                  {sourceOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(`emissionFactorEditor.${option.labelKey}`)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />

            <Typography variant="body2" color="text.secondary">
              {t("emissionFactorEditor.sourceDetailLabel")}
            </Typography>
            <Controller
              name="sourceDetail"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  variant="filled"
                  fullWidth
                  placeholder={t("emissionFactorEditor.sourceDetailPlaceholder")}
                  error={!!error}
                  {...field}
                />
              )}
            />

            <Typography variant="body2" color="text.secondary">
              {t("emissionFactorEditor.yearLabel")}
            </Typography>
            <Controller
              name="year"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  type="number"
                  variant="filled"
                  fullWidth
                  placeholder={t("emissionFactorEditor.yearPlaceholder")}
                  error={!!error}
                  {...field}
                />
              )}
            />

            <Typography variant="body2" color="text.secondary">
              {t("emissionFactorEditor.questionCodesLabel")}
            </Typography>
            <SearchQuestions
              onSelectQuestion={(questionCode) => {
                append({ id: "new", questionCode });
              }}
            />
            <Card color="grey">
              <CardContent>
                <Stack spacing={1}>
                  <Stack spacing={2}>
                    {fields.map((pair, index) => (
                      <Stack
                        key={pair.id}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <Controller
                          control={control}
                          name={`pairs.${index}.questionCode` as const}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              variant="filled"
                              placeholder={t(
                                "emissionFactorEditor.questionCodePlaceholder",
                              )}
                              fullWidth
                              size="small"
                              error={!!error}
                              {...field}
                            />
                          )}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => remove(index)}
                        >
                          <FeatherIcon icon="trash-2" width={16} height={16} />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => append({ id: "new", questionCode: "" })}
                    >
                      {t("emissionFactorEditor.addQuestionCode")}
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Button variant="outlined" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button variant="contained" type="submit">
                {isEdit ? t("common.save") : t("common.create")}
              </Button>
            </Stack>
          </Stack>
        </form>
      }
    />
  );
};

export default EmissionFactorEditor;
